import { createApi } from "@reduxjs/toolkit/query/react";
import { LandingPageData, Bill, GetBillsParams, BillsListResponse, CreateBillPayload, UpdateBillPayload } from "../../utils/types"; // Adjust path to your types file
import { baseQueryWithLogout } from "../baseQuery";

// !! IMPORTANT: Storing full cookie strings like this in client-side code is a security risk.
// Consider if your backend can set HttpOnly cookies or if you can use token-based auth.
// const STATIC_COOKIE_STRING ="remember_token=8|593631179b181a0de72d9bcdb3f72989b61a064f290da79888813b3adafd8c65461d727c00005470e8abe507dfc0e62b2499d572ce54a8fcf6cb2e2dc20a5656; session=eyJfZnJlc2giOmZhbHNlLCJfdXNlcl9pZCI6IjgifQ.aDbwng.Br0dT-uEH_Dju8ulsZW0POdTMDQ";

export const billsApi = createApi({
  // Renamed for broader scope, or keep as landingPageApi
  reducerPath: "billsApi", // Match the new name if you changed it
  baseQuery: baseQueryWithLogout,
  tagTypes: ["LandingPage", "Bill"], // Added 'Bill' tag type
  endpoints: (builder) => ({
    // --- Landing Page Endpoints ---
    getLandingPageData: builder.query<LandingPageData, void>({
      query: () => "/todos/1", // This was using JSONPlaceholder
      providesTags: ["LandingPage"],
    }),

    // --- Bill Endpoints ---

    // 1. Get all bills (with optional query parameters for filtering/pagination)
    getBills: builder.query<BillsListResponse, GetBillsParams | void>({
      query: (params) => ({
        url: "/bills", // Assuming endpoint is /bills
        params: params || {}, // Pass query parameters like ?page=1&status=Pending
      }),
      // Provides a general 'LIST' tag for bills, and individual tags for each bill.
      providesTags: (result) =>
        result && result.items ? [...result.items.map(({ id }) => ({ type: "Bill" as const, id })), { type: "Bill", id: "LIST" }] : [{ type: "Bill", id: "LIST" }],
    }),

    // 2. Get a single bill by its ID
    getBillById: builder.query<Bill, string | number>({
      // Argument is the bill ID
      query: (billId) => `/bills/${billId}`, // Assuming endpoint is /bills/{id}
      providesTags: (result, error, id) => [{ type: "Bill", id }],
    }),

    // 3. Create a new bill
    createBill: builder.mutation<Bill, CreateBillPayload>({
      // Returns the created Bill, takes NewBillPayload
      query: (newBill) => ({
        url: "/bills",
        method: "POST",
        body: newBill,
      }),
      // After creating a bill, invalidate the list of bills to trigger a re-fetch.
      invalidatesTags: [{ type: "Bill", id: "LIST" }],
    }),

    // 4. Edit/Update an existing bill
    editBill: builder.mutation<Bill, UpdateBillPayload>({
      // Returns updated Bill, takes UpdateBillPayload
      query: ({ id, ...patch }) => ({
        url: `/bills/${id}`,
        method: "PUT", // Or 'PATCH' if your API supports partial updates
        body: patch,
      }),
      // After updating, invalidate the specific bill's cache and the list.
      invalidatesTags: (result, error, { id }) => [
        { type: "Bill", id },
        { type: "Bill", id: "LIST" },
      ],
    }),

    // 5. Delete a bill
    deleteBill: builder.mutation<{ success: boolean; id: string | number }, string | number>({
      // Arg is billId, response confirms deletion
      query: (billId) => ({
        url: `/bills/${billId}`,
        method: "DELETE",
      }),
      // Example of transforming response if API returns empty or different success indicators
      // transformResponse: (response: any, meta, arg: string | number) => {
      //   return { success: meta?.response?.ok ?? false, id: arg };
      // },
      // After deleting, invalidate the specific bill's cache and the list.
      invalidatesTags: (result, error, id) => [
        { type: "Bill", id },
        { type: "Bill", id: "LIST" },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const { useGetLandingPageDataQuery, useGetBillsQuery, useGetBillByIdQuery, useCreateBillMutation, useEditBillMutation, useDeleteBillMutation } = billsApi; // Use the new name if you changed it
